import { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import { Input } from '../ui/Input';
import { formatCurrency, getCurrencySymbol, getCurrencyFlag, parseNumber } from '../../lib/formatters';
import { validateAmount, sanitizeAmount } from '../../lib/validators';
import { copyToClipboard } from '../../lib/utils';

interface AmountInputProps {
  currency: string;
  value: string;
  onChange: (currency: string, value: string) => void;
  onFocus?: (currency: string) => void;
  onBlur?: () => void;
  focused?: boolean;
  disabled?: boolean;
}

export function AmountInput({
  currency,
  value,
  onChange,
  onFocus,
  onBlur,
  focused = false,
  disabled = false
}: AmountInputProps) {
  const [copied, setCopied] = useState(false);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = sanitizeAmount(e.target.value);
    if (validateAmount(newValue)) {
      onChange(currency, newValue);
    }
  }, [currency, onChange]);

  const handleFocus = useCallback(() => {
    onFocus?.(currency);
  }, [currency, onFocus]);

  const handleCopy = useCallback(async () => {
    const numericValue = parseNumber(value);
    const formattedValue = formatCurrency(numericValue, currency);
    const success = await copyToClipboard(formattedValue);
    
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [value, currency]);

  const numericValue = parseNumber(value);
  const formattedValue = numericValue > 0 ? formatCurrency(numericValue, currency) : '';

  return (
    <div className={`
      group relative p-4 rounded-xl border transition-all duration-200
      ${focused 
        ? 'border-primary-500 shadow-lg shadow-primary-500/20 bg-primary-50 dark:bg-primary-900/20' 
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
      }
      ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
    `}>
      <div className="flex items-center space-x-3">
        {/* Currency Info */}
        <div className="flex items-center space-x-2 min-w-0 flex-shrink-0">
          <span className="text-2xl">{getCurrencyFlag(currency)}</span>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white text-sm">
              {currency}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {getCurrencySymbol(currency)}
            </div>
          </div>
        </div>

        {/* Input */}
        <div className="flex-1 min-w-0">
          <Input
            type="text"
            value={value}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={onBlur}
            placeholder="0"
            disabled={disabled}
            className={`
              text-right text-lg font-mono border-0 bg-transparent p-0 focus:ring-0
              ${focused ? 'text-primary-900 dark:text-primary-100' : ''}
            `}
          />
          {formattedValue && (
            <div className="text-xs text-gray-500 dark:text-gray-400 text-right mt-1">
              {formattedValue}
            </div>
          )}
        </div>

        {/* Copy Button */}
        {numericValue > 0 && (
          <button
            onClick={handleCopy}
            disabled={disabled}
            className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Copy to clipboard"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {/* Focus indicator */}
      {focused && (
        <div className="absolute inset-0 rounded-xl ring-2 ring-primary-500 ring-opacity-50 pointer-events-none" />
      )}
    </div>
  );
}